import { TICKET_PRIORITY_VALUES, type TicketPriority } from "@sagot/constants";
import {
  AUTO_ROUTE,
  NEW_TICKET_DESCRIPTION_MAX,
  NEW_TICKET_TITLE_MAX,
  NEW_TICKET_TITLE_MIN,
  UNSUPPORTED_FIELD_TYPES,
} from "./ticket-constants";
import type { ContactOut, FieldDefinitionOut, TicketCreate, TicketTypeOut, TypeFieldOut } from "./ticket-types";

/** Value of one custom field as the form holds it: text and numbers as typed, a switch, or the ticked options. */
export type FieldValue = string | boolean | readonly string[];

export interface NewTicketDraft {
  requesterMode: "existing" | "new";
  contact: ContactOut | null;
  newName: string;
  newEmail: string;
  /** Organisation of a brand-new contact. */
  organisationId: string;
  typeId: string;
  title: string;
  description: string;
  priority: TicketPriority;
  /** `AUTO_ROUTE` (routing rules decide) or a user id. */
  assignee: string;
  notify: boolean;
  fields: Readonly<Record<string, FieldValue>>;
}

export type ProblemKey = "requester" | "newName" | "newEmail" | "type" | "title" | `field:${string}`;
export type Problems = Partial<Record<ProblemKey, { key: string; values?: Record<string, string | number> }>>;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function emptyDraft(type: TicketTypeOut | undefined): NewTicketDraft {
  return {
    requesterMode: "existing",
    contact: null,
    newName: "",
    newEmail: "",
    organisationId: "",
    typeId: type?.id ?? "",
    title: "",
    description: "",
    priority: priorityOf(type),
    assignee: AUTO_ROUTE,
    notify: true,
    fields: {},
  };
}

export function priorityOf(type: TicketTypeOut | undefined): TicketPriority {
  return TICKET_PRIORITY_VALUES.find((value) => value === type?.default_priority) ?? "normal";
}

/** A custom field the form can ask for: its place on the type plus its definition (options, bounds). */
export interface FormField {
  field: TypeFieldOut;
  definition: FieldDefinitionOut | undefined;
}

/**
 * Fields staff can fill in here (site, order, contact, files and serial arrive with their own screens). The equipment picker
 * is offered only where Assets is available to the member (`equipment`): without the module or the plan the field is left out
 * of the form and of its validation, so a ticketing-only workspace never asks for equipment.
 */
export function askableFields(fields: readonly TypeFieldOut[], definitions: readonly FieldDefinitionOut[], equipment = true): FormField[] {
  return [...fields]
    .sort((a, b) => a.position - b.position)
    .filter((field) => !UNSUPPORTED_FIELD_TYPES.some((type) => type === field.type) && (equipment || field.type !== "asset"))
    .map((field) => ({ field, definition: definitions.find((definition) => definition.id === field.field_definition_id) }));
}

/** A conditional field shows only while its parent holds one of the values (TKT-052). */
export function isFieldVisible(field: TypeFieldOut, values: NewTicketDraft["fields"]): boolean {
  const parentKey: unknown = field.condition?.parent_key;
  const expected: unknown = field.condition?.values;
  if (typeof parentKey !== "string" || !Array.isArray(expected)) return true;
  const parent = values[parentKey];
  if (parent === undefined) return false;
  return expected.some((value) => String(value) === String(parent));
}

export function isChoiceList(value: FieldValue | undefined): value is readonly string[] {
  return Array.isArray(value);
}

const isBlank = (value: FieldValue | undefined): boolean => value === undefined || value === "" || (Array.isArray(value) && value.length === 0);

/** What is wrong with the draft, as i18n keys; empty when it can be sent. */
export function validateDraft(draft: NewTicketDraft, fields: readonly FormField[]): Problems {
  const problems: Problems = {};
  if (draft.requesterMode === "existing" && !draft.contact) problems.requester = { key: "service.tickets.new.errors.requester" };
  if (draft.requesterMode === "new") {
    if (!draft.newName.trim()) problems.newName = { key: "service.tickets.new.errors.newName" };
    if (draft.newEmail.trim() && !EMAIL.test(draft.newEmail.trim())) problems.newEmail = { key: "service.tickets.new.errors.newEmail" };
  }
  if (!draft.typeId) problems.type = { key: "service.tickets.new.errors.type" };
  if (draft.title.trim().length < NEW_TICKET_TITLE_MIN) problems.title = { key: "service.tickets.new.errors.title", values: { min: NEW_TICKET_TITLE_MIN } };
  for (const { field } of fields) {
    const required = field.staff_required && field.required_on === "create";
    if (required && isFieldVisible(field, draft.fields) && isBlank(draft.fields[field.key]) && field.type !== "bool") {
      problems[`field:${field.key}`] = { key: "service.tickets.new.errors.field" };
    }
  }
  return problems;
}

function fieldBody(form: FormField, value: FieldValue | undefined): string | number | boolean | string[] | undefined {
  const { field } = form;
  if (field.type === "bool") return field.staff_required || value === true ? value === true : undefined;
  if (isBlank(value)) return undefined;
  if (isChoiceList(value)) return [...value];
  if (field.type === "number" || field.type === "integer") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  if (field.type === "datetime" && typeof value === "string") return new Date(value).toISOString();
  return typeof value === "string" ? value.trim() : undefined;
}

/** The asset ids chosen in the type's equipment fields (visible ones only): they are linked to the ticket once it exists. */
export function assetIdsChosen(fields: readonly FormField[], values: NewTicketDraft["fields"]): string[] {
  const chosen: string[] = [];
  for (const form of fields) {
    if (form.field.type !== "asset" || !isFieldVisible(form.field, values)) continue;
    const value = values[form.field.key];
    if (typeof value === "string" && value !== "" && !chosen.includes(value)) chosen.push(value);
  }
  return chosen;
}

/** The `fields` object of the request: only the visible, filled-in ones. */
export function fieldsBody(fields: readonly FormField[], values: NewTicketDraft["fields"]): Record<string, string | number | boolean | string[]> {
  const body: Record<string, string | number | boolean | string[]> = {};
  for (const form of fields) {
    if (!isFieldVisible(form.field, values)) continue;
    const value = fieldBody(form, values[form.field.key]);
    if (value !== undefined) body[form.field.key] = value;
  }
  return body;
}

/** The `POST /v1/tickets` body for a ticket raised on behalf of a client; the server routes it like any other. */
export function createBody(draft: NewTicketDraft, fields: readonly FormField[]): TicketCreate {
  const body: TicketCreate = {
    type_id: draft.typeId,
    title: draft.title.trim().slice(0, NEW_TICKET_TITLE_MAX),
    description: draft.description.trim().slice(0, NEW_TICKET_DESCRIPTION_MAX),
    origin: "staff",
    priority: draft.priority,
    notify_client: draft.notify,
    fields: fieldsBody(fields, draft.fields),
  };
  if (draft.requesterMode === "existing" && draft.contact) {
    body.requester_contact_id = draft.contact.id;
    if (draft.contact.organisation_id) body.organisation_id = draft.contact.organisation_id;
  }
  if (draft.requesterMode === "new") {
    body.new_contact = { name: draft.newName.trim(), email: draft.newEmail.trim() || null };
    if (draft.organisationId) body.organisation_id = draft.organisationId;
  }
  if (draft.assignee !== AUTO_ROUTE) body.assignee_user_id = draft.assignee;
  return body;
}
